// Minimal fetch wrapper for the admin portal -> ADMIN backend service.
//
// Reads the base path from src/lib/env (VITE_API_BASE_URL) and attaches the
// in-memory admin JWT access token as an Authorization: Bearer header.
//
// ISOLATION: this portal talks ONLY to the admin Cloud Run service (separate
// IAM / ingress / audit). It must never call the public service. Admin tokens
// carry role claim "admin"; the backend enforces the role on every request.
//
// SECURITY: this UI never sees the master TikTok token or any client's raw proxy
// API key. The audit log it renders is already sanitized server-side.

import { getAccessToken, tryRefresh } from "../lib/auth";
import { API_BASE } from "../lib/env";

export interface ApiError {
  status: number;
  // Sanitized, admin-safe message only.
  message: string;
  // Parsed JSON error body when the backend returned one — used by surfaces
  // that consume STRUCTURED refusals (e.g. the Telegram chat-binding
  // {ok:false, reason, hint?} shapes) instead of a bare message string.
  body?: unknown;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  // JSON body; will be stringified.
  body?: unknown;
  // When true, do not attach the Authorization header (e.g. login).
  anonymous?: boolean;
  // Internal: prevents infinite refresh loops.
  _isRetry?: boolean;
}

function buildHeaders(opts: RequestOptions): Headers {
  const headers = new Headers(opts.headers);
  if (opts.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!opts.anonymous) {
    const token = getAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }
  return headers;
}

export async function apiFetch<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const init: RequestInit = {
    ...opts,
    headers: buildHeaders(opts),
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    // Send the HttpOnly refresh cookie set by the ADMIN backend.
    credentials: "include",
  };

  const res = await fetch(url, init);

  // On 401 for an authenticated request, attempt a single refresh-token
  // rotation then retry once.
  if (res.status === 401 && !opts.anonymous && !opts._isRetry) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return apiFetch<T>(path, { ...opts, _isRetry: true });
    }
  }

  if (!res.ok) {
    let message = res.statusText;
    let body: unknown;
    try {
      const data = (await res.json()) as { message?: string; error?: string };
      body = data;
      message = data.message ?? data.error ?? message;
    } catch {
      // non-JSON error body; keep statusText
    }
    const err: ApiError = { status: res.status, message, body };
    throw err;
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: "POST", body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: "PUT", body }),
  delete: <T>(path: string, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: "DELETE" }),
};
